import { Box, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PaperContainer from "../../../components/Containers/PaperContainer";
import NavigationDrawer from "../../../components/NavigationDrawer";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import appcolors from "../../../constants/Colors";
import { CURRENCY } from "../../../constants/Variables";
import useDebounce from "../../../hooks/useDebounce";
import { setDonationCurrentPage, setDonationPageLimit, setDonationTotalCount, setDonationTotalPages } from "../../../lib/slices/pagination/donationPageSlice";
import { getAllDonations } from "../../../services/donationApiFn";
import { handleOpenSnackBar, roundTo2Decimal } from "../../../utils";
import { DonationFilters } from "./Filter";
import { APP_ICONS } from "../../../assets";
import GlobalButton from "../../../components/CustomButton";
import { DonationExportDialog } from "./DonationExportDialog";
import { setDonationEndDate, setDonationOrganization, setDonationStartDate } from "../../../lib/slices/searchKeysSlice";
import RefreshIcon from "@mui/icons-material/Refresh";
// Header Data For Promotional Block Table
const headerData = [
    "createdAt",
    "orderId",
    "organizationName",
    "user",
    "orderSubTotal",
    "donationPercentage",
    "donationExpectedAmount",
    "donationActualAmount"
];

// Table Rows For Promotional Block Table
const tableRows = [
    "created_date",
    "donation_order_id",
    "donation_organization_name",
    "donation_user",
    "donation_sub_total",
    "donation_donation_percentage",
    "donation_donation_expected_amount",
    "donation_donation_actual_amount"
];

// Promotional Block Component
export function DonationPage() {
    // Dispatch Hook
    const dispatch = useDispatch();
    // Open Dialog Ref
    const openExportDialog = useRef(null);
    // Loading State
    const [isLoading, setIsLoading] = useState(true);
    // All Promotional Blocks
    const [allDonations, setAllDonations] = useState([]);
    // Total Donations
    const [donationSummary, setDonationSummary] = useState({});
    // Translation Hook
    const { t } = useTranslation(["donation", "common"]);
    // Search Keys
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    // Get Pagination for Promotional Blocks
    const pagination = useSelector((state) => state.donationPaginationReducer);
    // Debounced Search Term
    const debouncedSearchTerm = useDebounce(searchKeys.donationOrganization, 1000, true, () => {
        dispatch(setDonationCurrentPage(1));
    });

    // Fetch All Promotional Blocks
    const handleFetchAllDonations = async (search, page, limit, startDate, endDate, loading = true) => {
        try {
            const params = {
                search,
                page,
                limit,
                startDate,
                endDate,
            };
            setIsLoading(loading);
            const response = await getAllDonations(params);
            if (response.data.status === "success") {
                let data = response?.data.data?.data
                let summary = response?.data.data?.summary;
                const pagination = response?.data.data.pagination;
                setDonationSummary(summary);
                setAllDonations(data);
                dispatch(setDonationPageLimit(pagination.limit));
                dispatch(setDonationCurrentPage(pagination.page));
                dispatch(setDonationTotalCount(pagination.total));
                dispatch(setDonationTotalPages(pagination.totalPages));
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch All Promotional Blocks On Component Mount
    useEffect(() => {
        handleFetchAllDonations(
            debouncedSearchTerm,
            pagination.page,
            pagination.limit,
            searchKeys.donationStartDate,
            searchKeys.donationEndDate,
        );
    }, [
        debouncedSearchTerm,
        pagination.page,
        pagination.limit,
    ]);


    // handle page change
    const handlePageChange = (event, newPage) => {
        const page = newPage + 1;
        dispatch(setDonationCurrentPage(page));
    };

    // handle rows per page change
    const handleRowsPerPageChange = (event) => {
        console.log(event, "event");
        const limit = parseInt(event.target.value, 10);
        dispatch(setDonationPageLimit(limit));
        dispatch(setDonationCurrentPage(1));
    };

    // handle reset filters
    const handleResetFilters = () => {
        dispatch(setDonationOrganization(""));
        dispatch(setDonationStartDate(null));
        dispatch(setDonationEndDate(null));
        dispatch(setDonationCurrentPage(1));
        handleFetchAllDonations("", 1, pagination.limit, null, null);
    };

    // // Prepare Export Data For Promotional Block
    // const prepareExportData = () => {
    //     return allCharityOrganizations?.map((item) => ({
    //         "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
    //         "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
    //         "Name In Romanian": item.name?.ro || "-",
    //         "Name In English": item.name?.en || "-",
    //         "Description In Romanian": item.description?.ro || "-",
    //         "Description In English": item.description?.en || "-",
    //         "Status": item.status ? "Active" : "Inactive",
    //     }));
    // };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"export_button"}
                    btnText={"export"}
                    startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                    handleClickBtn={() => {
                        openExportDialog.current.openDialog();
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />
                <GlobalButton
                    variant={"export_button"}
                    btnText={""}
                    startIcon={<RefreshIcon fontSize="small" />}
                    handleClickBtn={handleResetFilters}
                    styleProps={{
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        p: 0,
                        "& .MuiButton-startIcon": {
                            margin: 0,
                        },
                    }}
                />
            </Stack>
        );
    };

    const renderTable = useMemo(() => {
        return (
            <DynamicPaginatedTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allDonations || []}
                showActions={true}
                paginatedData={pagination}
                showDynamicPagination={true}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 30, 50, 100]}
                isLoading={isLoading}
            />
        );
    }, [allDonations, isLoading]);

    const summaryData = useMemo(
        () => [
            {
                label: t("donationExpectedAmount", { ns: "common" }),
                value: roundTo2Decimal(donationSummary?.totalDonationAmount ?? 0),
            },
            {
                label: t("donationActualAmount", { ns: "common" }),
                value: roundTo2Decimal(donationSummary?.totalActualAmount ?? 0),
            },
        ],
        [donationSummary, t],
    );

    const renderDonationSummary = useMemo(() => {
        return (
            <Stack
                mb={2}
                direction={{ xs: "column", sm: "row" }}
                gap={2}
            >
                {summaryData.map((item, index) => (
                    <Stack
                        key={item.label}
                        flex={1}
                        p={2.2}
                        borderRadius={"14px"}
                        boxShadow={"0px 8px 22px rgba(21, 112, 239, 0.16)"}
                        sx={{
                            background: "linear-gradient(135deg, #EEF5FF 0%, #F7FBFF 100%)",
                            border: "1px solid #DDEBFF",
                        }}
                        maxWidth={{ xs: "100%", sm: 360 }}
                    >
                        <Stack
                            direction={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                        >
                            <Box>
                                <Typography variant={"body2"} color={appcolors.crayolaGrey}>
                                    {item.label}
                                </Typography>
                                <Box mt={0.5}>
                                    <Typography variant={"h5"} fontWeight={700} color={"#0D47A1"}>
                                        {item.value} {CURRENCY}
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack
                                width={44}
                                height={44}
                                borderRadius={"50%"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                sx={{
                                    background: index === 0
                                        ? "linear-gradient(135deg, #1570EF 0%, #4C9AFF 100%)"
                                        : "linear-gradient(135deg, #0D9488 0%, #2DD4BF 100%)",
                                    boxShadow: index === 0
                                        ? "0px 6px 14px rgba(21, 112, 239, 0.28)"
                                        : "0px 6px 14px rgba(13, 148, 136, 0.28)",
                                }}
                            >
                                <Typography
                                    variant={"body1"}
                                    color={appcolors.white}
                                    fontWeight={800}
                                >
                                    {CURRENCY}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        );
    }, [summaryData]);

    return (
        <NavigationDrawer>
            <PaperContainer
                title={t("donations")}
                subtitle={t("viewDonations")}
                headerActions={renderHeaderAction()}
                searchActions={<DonationFilters fetchData={handleFetchAllDonations} />}
            >
                {renderDonationSummary}
                {renderTable}
            </PaperContainer>

            <DonationExportDialog ref={openExportDialog} />

        </NavigationDrawer>
    );
}


